import React, { useState } from 'react';
import styled from 'styled-components';
import { RelativeDatePicker } from 'components/FormFields';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';
import PropsTable from '../../components/PropsTable';

const PageTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const PageDescription = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 48px 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  margin: 0 0 24px 0;
`;

const SelectedValue = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB'};
  border-radius: 8px;
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};

  code {
    color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  }
`;

const RelativeDatePickerDocs = () => {
  const [value1, setValue1] = useState('2 weeks before event');
  const [time1, setTime1] = useState('23:59');
  const [value2, setValue2] = useState('3 days before event');
  const [time2, setTime2] = useState('09:00');
  const [value3, setValue3] = useState('4 hours before event');

  const props = [
    {
      name: 'value',
      type: 'string',
      default: '"1 week before event"',
      required: false,
      description:
        'The relative date string (e.g., "2 weeks before event", "3 days before event")',
    },
    {
      name: 'onChange',
      type: 'function',
      required: false,
      description:
        'Callback when value changes. Receives formatted string like "2 weeks before event".',
    },
    {
      name: 'time',
      type: 'string',
      default: '"23:59"',
      required: false,
      description: 'Time value in 24-hour format (e.g., "23:59", "09:00")',
    },
    {
      name: 'onTimeChange',
      type: 'function',
      required: false,
      description: 'Callback when time changes. Receives time string.',
    },
    {
      name: 'onUnitChange',
      type: 'function',
      required: false,
      description:
        'Callback when the unit (hour/day/week) changes. Receives unit string.',
    },
    {
      name: 'showTime',
      type: 'boolean',
      default: 'true',
      required: false,
      description:
        'Whether to show the time picker. Auto-hidden when unit is "hour".',
    },
  ];

  return (
    <>
      <PageTitle>RelativeDatePicker</PageTitle>
      <PageDescription>
        A compact inline component for selecting relative date offsets (e.g.,
        &quot;2 weeks before event&quot;). Used in ticket templates where actual
        event dates aren&apos;t known yet. Displays as: [number] [unit dropdown]
        at [time picker].
      </PageDescription>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <PropsTable props={props} />
      </Section>

      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <ComponentDemo label="Relative date with time" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <RelativeDatePicker
              value={value1}
              onChange={setValue1}
              time={time1}
              onTimeChange={setTime1}
            />
            <SelectedValue>
              Value: <code>{value1}</code> at <code>{time1}</code>
            </SelectedValue>
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`import { RelativeDatePicker } from 'components/FormFields';

const [value, setValue] = useState('2 weeks before event');
const [time, setTime] = useState('23:59');

<RelativeDatePicker
  value={value}
  onChange={setValue}
  time={time}
  onTimeChange={setTime}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Days with Morning Time</SectionTitle>
        <ComponentDemo label="Days unit with custom time" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <RelativeDatePicker
              value={value2}
              onChange={setValue2}
              time={time2}
              onTimeChange={setTime2}
            />
            <SelectedValue>
              Value: <code>{value2}</code> at <code>{time2}</code>
            </SelectedValue>
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Hours (No Time Picker)</SectionTitle>
        <ComponentDemo
          label="Time picker auto-hides for hour unit"
          align="stretch"
        >
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <RelativeDatePicker value={value3} onChange={setValue3} />
            <SelectedValue>
              Value: <code>{value3}</code>
            </SelectedValue>
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`// Time picker is auto-hidden when unit is "hour"
<RelativeDatePicker
  value="4 hours before event"
  onChange={setValue}
/>

// Explicitly hide time picker
<RelativeDatePicker
  value={value}
  onChange={setValue}
  showTime={false}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Import</SectionTitle>
        <CodeBlock>
          {`import { RelativeDatePicker } from 'components/FormFields';
// or
import RelativeDatePicker from 'components/FormFields/RelativeDatePicker';`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Features</SectionTitle>
        <ul style={{ lineHeight: 1.8 }}>
          <li>Compact inline layout: [number] [unit] at [time]</li>
          <li>Supports hours, days, and weeks units</li>
          <li>Time picker auto-hides for hour-based offsets</li>
          <li>Parses and formats relative date strings automatically</li>
          <li>Number input clamped to 1-99 range</li>
          <li>Theme-aware (light/dark mode)</li>
        </ul>
      </Section>

      <Section>
        <SectionTitle>When to Use</SectionTitle>
        <ul style={{ lineHeight: 1.8 }}>
          <li>
            <strong>RelativeDatePicker</strong> - When dates are relative to an
            event (e.g., ticket sale start/end in templates)
          </li>
          <li>
            <strong>DateSelect</strong> - When you need an absolute date
            (MM-DD-YYYY)
          </li>
          <li>
            <strong>DateTimePicker</strong> - When you need both date and time
            with a calendar UI
          </li>
        </ul>
      </Section>
    </>
  );
};

export default RelativeDatePickerDocs;
